import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import React from 'react';
import { FormHelperText, FormLabel } from '@mui/material';
import { Controller } from 'react-hook-form';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface Props {
  id: string;
  labelText?: string;
  placeHolderText?: string;
  name?: string;
  error?: boolean;
  helperText?: any;
  defaultValue?: string;
  disabled?: boolean;
  value?: any;
  onChange?: any;
  options: any;
  control?: null | any;
  isError?: boolean;
  requiredField?: boolean;
  studioFilter?: boolean;
  lableName?: any;
  maxWidth?: boolean;
  width?: 'auto' | 'full' | undefined;
  border?: boolean;
  roundedBorder?: boolean;
  selectedAlready?: boolean;
}
/**
 * A customizable select component that supports controlled and uncontrolled modes, option rendering, and styling.
 * @example
 * CustomSelect({ id: 'example-select', labelText: 'Example Select', options: ['Option 1', 'Option 2'], onChange: handleSelectChange })
 * Returns a select input with provided options.
 * @param {Object} props - The properties object.
 * @param {string} props.id - The identifier for the select element.
 * @param {string} props.labelText - The text label for the select field.
 * @param {string} props.placeHolderText - The placeholder text displayed when no option is selected.
 * @param {string} [props.name=''] - The name attribute for the select, used in forms.
 * @param {boolean} props.error - Flag to indicate if there's an error in the selection.
 * @param {string} props.helperText - Helper text displayed below the select component.
 * @param {string} props.defaultValue - The default value shown in the select when no option is chosen.
 * @param {boolean} props.disabled - Boolean to disable interaction with the select.
 * @param {string} props.value - The currently selected value of the select component.
 * @param {function} props.onChange - Callback function to handle the change event.
 * @param {Array} props.options - Array of options to be rendered in the dropdown.
 * @param {Object} props.control - Control object for integrating with form libraries like React Hook Form.
 * @param {boolean} props.isError - Indicates if there is an error state for custom styling.
 * @param {boolean} props.requiredField - Boolean indicating if the field is mandatory.
 * @param {boolean} props.studioFilter - Custom prop to alter select behavior and rendering.
 * @param {string} props.lableName - Used to render custom option labels.
 * @param {boolean} props.maxWidth - Determines if select items should have restricted width.
 * @param {string} props.width - Width definition for the select, 'auto' for dynamic sizing.
 * @param {boolean} [props.border=true] - Boolean to toggle border visibility.
 * @param {boolean} props.roundedBorder - Apply rounded borders if true.
 * @param {boolean} [props.selectedAlready=false] - If set, affects the placeholder rendering.
 * @returns {JSX.Element} The rendered select component with custom styles and behavior.
 * @description
 *   - Manages both controlled and uncontrolled modes of operation.
 *   - Utilize React Hook Form's Controller for controlled forms.
 *   - Supports custom rendering of option labels using lableName.
 *   - Handles display and placeholder logic for empty or unselected state.
 */
export default function CustomSelect(props: Props) {
  const {
    id,
    labelText,
    placeHolderText,
    name = '',
    error,
    helperText,
    defaultValue,
    disabled,
    value,
    onChange,
    options,
    control,
    isError,
    requiredField,
    studioFilter,
    lableName,
    maxWidth,
    width,
    border = true,
    roundedBorder,
    selectedAlready = false,
  } = props;

  const [currentValue, setcurrentValue] = React.useState('');
  const handleChange = (event: SelectChangeEvent) => {
    onChange(event);
    setcurrentValue(event.target.value as string);
  };
  const [open, setOpen] = React.useState(true);

  return (
    <FormControl
      fullWidth={width === 'auto' ? false : true}
      className={`customSelect ${!border ? 'no_border' : null} ${
        roundedBorder && 'roundedBorder'
      }`}
      error={error ? true : false}
    >
      {labelText && (
        <FormLabel className="formLabel">
          {labelText} {requiredField ? <sup>*</sup> : null}{' '}
        </FormLabel>
      )}

      {control ? (
        <Controller
          name={name}
          control={control}
          render={({ field }) =>
            field && (
              <Select
                //   open={open}
                // onOpen={() => setOpen(true)}
                // onClose={() => setOpen(false)}
                labelId={id}
                // defaultValue={defaultValue}
                disabled={disabled}
                size="small"
                id="select"
                placeholder={placeHolderText}
                displayEmpty
                renderValue={selected => {
                  if (!selected) {
                    return (
                      <div className="select_placeholder">
                        {placeHolderText}
                      </div>
                    );
                  }
                  if (selected.length === 0) {
                    return (
                      <div className="select_placeholder">
                        {placeHolderText}
                      </div>
                    );
                  }
                  return selected;
                }}
                {...field}
                value={field.value}
                IconComponent={KeyboardArrowDownIcon}
                className={`selectHeight`}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: '150px',
                    },
                  },
                }}
              >
                {options.map((item: any) => {
                  return (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  );
                })}
              </Select>
            )
          }
        />
      ) : studioFilter ? (
        <Select
          // open={open}
          // onOpen={() => setOpen(true)}
          // onClose={() => setOpen(false)}
          labelId={id}
          id="select"
          size="small"
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          disabled={disabled}
          displayEmpty
          // renderValue={(selected) => {
          //   if (selected.length === 0) {
          //     return <div className="select_placeholder">{labelText}</div>
          //   }
          //   return options?.find((item: any) => item._id === selected)?.[
          //     lableName
          //   ]
          // }}
          renderValue={selected => {
            if (!selected) {
              return (
                <div className={selectedAlready ? '' : 'select_placeholder'}>
                  {placeHolderText}
                </div>
              );
            }
            if (selected.length === 0) {
              return (
                <div className={selectedAlready ? '' : 'select_placeholder'}>
                  {placeHolderText}
                </div>
              );
            }
            return (
              options?.find((item: any) => item._id === selected)?.[
                lableName
              ] || defaultValue
            );
          }}
          placeholder={placeHolderText}
          IconComponent={KeyboardArrowDownIcon}
          className={`selectHeight`}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: '150px',
              },
            },
          }}
        >
          {options?.length > 0 ? (
            options.map((item: any) => (
              <MenuItem
                key={item._id}
                value={item._id}
                className={maxWidth ? 'maxWidth' : ''}
              >
                {item[lableName]}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No options found</MenuItem>
          )}
        </Select>
      ) : (
        <Select
          labelId={id}
          id="select"
          size="small"
          value={value}
          disabled={disabled}
          defaultValue={defaultValue}
          onChange={onChange}
          placeholder={placeHolderText}
          IconComponent={KeyboardArrowDownIcon}
          displayEmpty
          renderValue={selected => {
            if (selected?.length === 0) {
              return (
                <div className="select_placeholder">{placeHolderText}</div>
              );
            }
            return selected;
          }}
          className="selectHeight"
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: '150px',
              },
            },
          }}
        >
          {options.map((item: any) => {
            return (
              <MenuItem value={item} className={maxWidth ? 'maxWidth' : ''}>
                {item}
              </MenuItem>
            );
          })}
        </Select>
      )}

      <FormHelperText className={error ? 'Mui-error' : ''}>
        {helperText}
      </FormHelperText>
    </FormControl>
  );
}
